import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabase';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TiboLogo from '../../../components/assets/tibo-logo.svg';
import { useAuth } from '../../../contexts/AuthContext';

export function InviteSignup() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  // Cargar información del negocio
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      if (!businessId) {
        setError('ID de negocio no válido');
        setStatus('error');
        return;
      }

      try {
        setLoading(true);
        console.log('Verificando negocio:', businessId);
        
        // Guardar el ID del negocio en localStorage para usarlo después de la autenticación
        localStorage.setItem('pendingJoinBusinessId', businessId);
        
        // Obtener datos del negocio
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, owner_id')
          .eq('id', businessId)
          .single();

        if (businessError) {
          console.error('Error al obtener negocio:', businessError);
          throw businessError;
        }
        
        console.log('Negocio encontrado:', businessData);
        setBusiness(businessData);
        setStatus('ready');

        // Si el usuario ya está autenticado, proceder a crear la solicitud
        if (user) {
          console.log('Usuario ya autenticado, procesando solicitud...');
          checkAndProcessJoinRequest(businessData);
        }
      } catch (error) {
        console.error('Error al verificar negocio:', error);
        setError(error.message);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [businessId]);

  // Verificar y procesar solicitud si el usuario está autenticado
  useEffect(() => {
    if (user && business && status === 'ready') {
      checkAndProcessJoinRequest(business);
    }
  }, [user, business, status]);

  // Función para autenticarse con Google
  const handleGoogleLogin = async () => {
    try {
      console.log('Iniciando login con Google desde invitación...');
      setAuthLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback?invitation=true`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
  
      if (error) throw error;
    } catch (err) {
      console.error('Error al iniciar sesión:', err);
      setError('Error al iniciar sesión. Por favor intenta nuevamente.');
    } finally {
      setAuthLoading(false);
    }
  };

  // Función para verificar si el usuario ya es miembro o tiene solicitud pendiente
  const checkAndProcessJoinRequest = async (businessData) => {
    if (!user || !businessData) return;
    
    try {
      setStatus('checking');
      
      // 1. Verificar si ya es miembro
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (profile) {
        // Verificar si ya es miembro del negocio
        const { data: membership, error: membershipError } = await supabase
          .from('business_members')
          .select('id')
          .eq('business_id', businessData.id)
          .eq('profile_id', profile.id)
          .single();

        if (membershipError && membershipError.code !== 'PGRST116') {
          throw membershipError;
        }

        if (membership) {
          setError('Ya eres miembro de este negocio');
          setStatus('error');
          setTimeout(() => navigate('/'), 2000);
          return;
        }
      }

      // 2. Verificar si ya tiene una solicitud pendiente
      const { data: existingRequest, error: requestCheckError } = await supabase
        .from('business_join_requests')
        .select('id, status')
        .eq('business_id', businessData.id)
        .eq('user_id', user.id)
        .single();

      if (requestCheckError && requestCheckError.code !== 'PGRST116') {
        throw requestCheckError;
      }

      if (existingRequest) {
        if (existingRequest.status === 'pending') {
          setError('Ya tienes una solicitud pendiente');
        } else if (existingRequest.status === 'rejected') {
          setError('Tu solicitud fue rechazada');
        }
        setStatus('error');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      // 3. Crear solicitud de unión
      setStatus('requesting');
      
      const { error: requestError } = await supabase
        .from('business_join_requests')
        .insert([{
          business_id: businessData.id,
          user_id: user.id,
          status: 'pending',
          user_email: user.email,
          user_name: user.user_metadata?.name || user.email.split('@')[0]
        }]);

      if (requestError) {
        throw requestError;
      }

      // 4. Mostrar mensaje de éxito
      setStatus('success');
      toast.success('Solicitud enviada exitosamente');
      
      // Esperar un momento y redirigir
      setTimeout(() => navigate('/'), 2000);

    } catch (error) {
      console.error('Error al procesar solicitud:', error);
      setError(error.message);
      setStatus('error');
      setTimeout(() => navigate('/'), 2000);
    }
  };

  // Mensajes según el estado
  const statusMessages = {
    loading: 'Cargando información del negocio...',
    ready: 'Listo para unirte',
    checking: 'Verificando tu información...',
    requesting: 'Enviando solicitud...',
    success: '¡Solicitud enviada! Serás redirigido en breve.',
    error: error || 'Error al procesar la solicitud'
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      <img src={TiboLogo} alt="Tibó" className="h-12 mb-8" />
      
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
        {loading ? (
          <div className="flex flex-col items-center justify-center space-y-4 py-8">
            <Loader2 className="h-8 w-8 text-[#3B7C5B] animate-spin" />
            <p className="text-gray-600 dark:text-gray-400">Cargando información...</p>
          </div>
        ) : (
          <>
            {/* Icono del negocio */}
            <div className="w-20 h-20 bg-[#3B7C5B]/10 rounded-full mx-auto mb-6 flex items-center justify-center">
              <Building2 className="w-10 h-10 text-[#3B7C5B]" />
            </div>
            
            {business ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {business.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Has sido invitado a unirte al equipo de <span className="font-medium">{business.name}</span>
                </p>
                
                {!user && status === 'ready' && (
                  <div className="mb-6">
                    <button
                      onClick={handleGoogleLogin}
                      disabled={authLoading}
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-all shadow-sm hover:shadow-md"
                    >
                      {authLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              fill="#4285F4"
                            />
                            <path
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              fill="#34A853"
                            />
                            <path
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              fill="#FBBC05"
                            />
                            <path
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              fill="#EA4335"
                            />
                          </svg>
                          <span className="font-medium">Continuar con Google</span>
                        </>
                      )}
                    </button>
                    
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                      Tibó es una plataforma que te permite gestionar inventario y ventas de forma sencilla junto a tu equipo.
                    </p>
                  </div>
                )}
                
                {(user || status !== 'ready') && (
                  <div className="mt-4">
                    <p className={`text-sm ${status === 'error' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
                      {statusMessages[status]}
                    </p>
                    
                    {status === 'success' && (
                      <div className="mt-4 flex items-center justify-center">
                        <Loader2 className="h-5 w-5 text-[#3B7C5B] animate-spin mr-2" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Redirigiendo...</span>
                      </div>
                    )}
                    
                    {status === 'error' && (
                      <button
                        onClick={() => navigate('/')}
                        className="mt-4 px-4 py-2 text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 rounded-lg transition-colors"
                      >
                        Volver al inicio
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="py-8">
                <h2 className="text-xl font-bold text-red-500 mb-3">
                  Negocio no encontrado
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  No pudimos encontrar el negocio al que intentas unirte.
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  Volver al inicio
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
